import React, { useMemo } from 'react';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  FilePlus,
  Info,
  Package,
  RotateCcw,
  Ship,
  Truck,
  MapPin,
} from 'lucide-react';
import { useDirection } from '@/contexts/DirectionContext';
import { etaLabel } from '@/features/dashboard/data/claudeMockData';
import type { ContainerModalData } from '../interfaces/container.interface';
import { portName, visibleMovements } from '../utils/containerVoyage';

type Lang = 'ro' | 'en';
type Tone = 'info' | 'success' | 'hub' | 'neutral' | 'warn';

interface ContainerEventsTabProps {
  data: ContainerModalData;
  lang: Lang;
}

interface EventRow {
  key: string;
  date: string | null;
  code: string | null;
  title: string;
  place: string | null;
  vessel: string | null;
  tone: Tone;
  icon: React.ElementType;
  source: string;
}

const MOVEMENTS: Record<string, { ro: string; en: string; tone: Tone; icon: React.ElementType }> = {
  ES: { ro: 'Container gol trimis la expeditor', en: 'Empty container sent to shipper', tone: 'warn', icon: Package },
  FL: { ro: 'Container plin intrat în terminal', en: 'Full container gated in', tone: 'warn', icon: Package },
  OF: { ro: 'Încărcat pe navă', en: 'Loaded on vessel', tone: 'info', icon: ArrowUpFromLine },
  OE: { ro: 'Încărcat gol pe navă', en: 'Loaded empty on vessel', tone: 'info', icon: ArrowUpFromLine },
  DF: { ro: 'Descărcat de pe navă', en: 'Discharged from vessel', tone: 'success', icon: ArrowDownToLine },
  DE: { ro: 'Descărcat gol de pe navă', en: 'Discharged empty from vessel', tone: 'success', icon: ArrowDownToLine },
  FC: { ro: 'Livrat plin la importator', en: 'Delivered full to importer', tone: 'hub', icon: Truck },
  IE: { ro: 'Redirecționat gol către un exportator', en: 'Redirected empty to an exporter', tone: 'neutral', icon: RotateCcw },
  RE: { ro: 'Returnat gol în terminal', en: 'Returned empty to terminal', tone: 'neutral', icon: RotateCcw },
};

export const ContainerEventsTab: React.FC<ContainerEventsTabProps> = ({ data, lang }) => {
  const { direction } = useDirection();
  const isRo = lang === 'ro';
  const locale = isRo ? 'ro-RO' : 'en-GB';

  const rows = useMemo<EventRow[]>(() => {
    const port = (code: string | null | undefined) => {
      if (!code) return null;
      const name = portName(data, code);
      return name ? `${name} (${code})` : code;
    };
    const moves = visibleMovements(data, direction);
    const out: EventRow[] = moves.map((m, i) => {
      const def = MOVEMENTS[m.movement];
      const loaded = m.movement === 'OF' || m.movement === 'OE';
      const place = loaded
        ? [port(m.loadingPort), port(m.dischargePort)].filter(Boolean).join(' → ')
        : port(m.loadingPort || m.dischargePort || m.terminal);
      return {
        key: `m_${m.id || i}`,
        date: m.date,
        code: m.movement,
        title: def ? (isRo ? def.ro : def.en) : m.movement,
        place: place || null,
        vessel: [m.vesselName, m.vesselCode].filter(Boolean).join(' · ') || null,
        tone: def?.tone ?? 'neutral',
        icon: def?.icon ?? Info,
        source: 'Yang Ming',
      };
    });

    // Datele din booking completează doar ce Yang Ming n-a raportat încă.
    if (data.booking.onBoardDate && !moves.some((m) => m.movement === 'OF')) {
      out.push({
        key: 'onboard',
        date: data.booking.onBoardDate,
        code: null,
        title: isRo ? 'Plecat pe mare' : 'Departed by sea',
        place: port(data.route.pol),
        vessel: null,
        tone: 'info',
        icon: Ship,
        source: 'TLS',
      });
    }
    const pod = data.route.pod;
    const dfAtPod = moves.some((m) => m.movement === 'DF' && (m.loadingPort || m.dischargePort) === pod);
    if (data.booking.dischargeDate && !dfAtPod) {
      out.push({
        key: 'discharge',
        date: data.booking.dischargeDate,
        code: null,
        title: isRo ? 'Descărcat în portul de destinație' : 'Discharged at destination',
        place: port(pod),
        vessel: null,
        tone: 'success',
        icon: ArrowDownToLine,
        source: 'TLS',
      });
    }
    if (data.booking.creationDate) {
      out.push({
        key: 'created',
        date: data.booking.creationDate,
        code: null,
        title: isRo ? 'Booking creat' : 'Booking created',
        place: null,
        vessel: null,
        tone: 'neutral',
        icon: FilePlus,
        source: 'TLS',
      });
    }

    const time = (d: string | null) => (d ? new Date(d).getTime() : 0);
    return out.sort((a, b) => time(b.date) - time(a.date));
  }, [data, direction, isRo]);

  if (rows.length === 0) {
    return (
      <div className="empty">
        <div className="icon">
          <Info size={22} strokeWidth={2} />
        </div>
        <div className="fw-600">{isRo ? 'Niciun eveniment' : 'No events'}</div>
        <div className="muted fs-12 mt-1">
          {isRo ? 'Evenimentele apar pe măsură ce containerul avansează.' : 'Events appear as the container progresses.'}
        </div>
      </div>
    );
  }

  // Grupare pe lună, cele mai noi primele.
  const groups: Array<{ label: string; rows: EventRow[] }> = [];
  rows.forEach((r) => {
    const label = r.date
      ? new Date(r.date).toLocaleDateString(locale, { month: 'long', year: 'numeric' })
      : isRo ? 'Fără dată' : 'No date';
    const g = groups[groups.length - 1];
    if (g && g.label === label) g.rows.push(r);
    else groups.push({ label, rows: [r] });
  });

  const latest = rows.find((r) => r.source === 'Yang Ming' && r.date);
  const yangMingCount = rows.filter((r) => r.source === 'Yang Ming').length;

  return (
    <div>
      <div className="evl-head">
        <span>
          <strong>{rows.length}</strong> {isRo ? (rows.length === 1 ? 'eveniment' : 'evenimente') : rows.length === 1 ? 'event' : 'events'}
          <span className="muted">
            {' · '}
            {yangMingCount} {isRo ? 'raportate de Yang Ming' : 'reported by Yang Ming'}
          </span>
        </span>
        {latest?.date && (
          <span className="muted">
            {isRo ? 'Ultima mișcare' : 'Latest movement'}: {etaLabel(latest.date, lang)}
          </span>
        )}
      </div>

      {groups.map((g) => (
        <div key={g.label} className="evl-group">
          <div className="evl-month">{g.label}</div>
          <div className="evl-list">
            {g.rows.map((r) => {
              const d = r.date ? new Date(r.date) : null;
              const Icon = r.icon;
              const hasTime = !!d && r.source === 'Yang Ming' && (d.getHours() !== 0 || d.getMinutes() !== 0);
              return (
                <div key={r.key} className={'evl-row' + (r === latest ? ' latest' : '')}>
                  <div className="evl-date">
                    <span className="day">{d ? d.getDate() : '—'}</span>
                    <span className="mon">{d ? d.toLocaleDateString(locale, { month: 'short' }) : ''}</span>
                    {hasTime && (
                      <span className="hm">
                        {d!.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div className={'evl-icon ' + r.tone}>
                    <Icon size={15} strokeWidth={2.2} />
                  </div>
                  <div className="evl-body">
                    <div className="evl-title">
                      <span>{r.title}</span>
                      {r.code && <span className="evl-code mono">{r.code}</span>}
                      {r === latest && <span className="evl-latest">{isRo ? 'Cea mai recentă' : 'Latest'}</span>}
                    </div>
                    <div className="evl-meta">
                      {r.place && (
                        <span>
                          <MapPin size={11} strokeWidth={2} />
                          {r.place}
                        </span>
                      )}
                      {r.vessel && (
                        <span>
                          <Ship size={11} strokeWidth={2} />
                          {r.vessel}
                        </span>
                      )}
                      <span className="evl-source">{r.source}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
